import axios from 'axios';
import { getRoleValue } from '../../../../utils/roleUtils';

const BASE_URL = process.env.REACT_APP_BASE_URL;
const DEFAULT_TIMEOUT = 8000;

const GROUP_TYPE = {
  UNSPECIFIED: 0,
  TEAM: 1,
  DEPARTMENT: 2,
  CLIENT: 3,
  ADMIN: 4
};

const USER_STATUS = {
  ACTIVE: 0,
  PENDING: 1,
  DISABLED: 2
};

const SERVICE_KEY = {
  UNSPECIFIED: 0,
  PIOAGENT: 1
};

const CLIENT_ROLE = {
  UNSPECIFIED: 0,
  MEMBER: 1,
  ADMIN: 2
};

const DEPARTMENT_ROLE = {
  UNSPECIFIED: 0,
  MEMBER: 1,
  ADMIN: 2
};

const TEAM_ROLE = {
  UNSPECIFIED: 0,
  MEMBER: 1,
  LEAD: 2
};

export { GROUP_TYPE, USER_STATUS, SERVICE_KEY, CLIENT_ROLE, DEPARTMENT_ROLE, TEAM_ROLE };

const getHeaders = (token) => ({
  'Authorization': `Bearer ${token}`,
  'Content-Type': 'application/json'
});

const handleError = (error) => {
  console.error('User API Error:', error);
  if (error.response?.status === 503) {
    return { success: false, error: 'Service temporarily unavailable' };
  }
  return {
    success: false,
    error: error.response?.data?.message || error.message || 'Operation failed'
  };
};

export const registerRoles = async (serviceKey, roles, token) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/user/v1/service/roles`,
      { service_key: serviceKey, roles },
      { headers: getHeaders(token) }
    );
    return {
      success: response.data.status === 0,
      id: response.data.id,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const listServiceRoles = async (serviceKey, token) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/user/v1/service/roles?service_key=${serviceKey}`,
      { headers: getHeaders(token) }
    );
    return {
      success: response.data.status === 0,
      roles: response.data.roles,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const createUser = async (userData, token) => {
  try {
    const payload = {
      first_name: userData.firstName,
      last_name: userData.lastName,
      email: userData.email,
      password: userData.password,
      phone: userData.phone,
      company: userData.company,
      position: userData.position,
      department: userData.department,
      team: userData.team,
      status: userData.status ?? USER_STATUS.ACTIVE
    };

    const response = await axios.post(
      `${BASE_URL}/user/public/v1/user`,
      payload,
      { headers: { 'Content-Type': 'application/json' } }
    );

    return {
      success: response.data.status === 0,
      user: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getUserById = async (userId, token) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/user/v1/user/${userId}`,
      { headers: getHeaders(token) }
    );
    return {
      success: response.data.status === 0,
      user: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getUsersByIDs = async (userIds, token) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/user/v1/users/list`,
      { ids: userIds },
      { headers: getHeaders(token) }
    );
    return {
      success: response.data.status === 0,
      users: response.data.users,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getCurrentUser = async (token) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/user/v1/current/user`,
      { headers: getHeaders(token), timeout: DEFAULT_TIMEOUT }
    );
    return {
      success: response.data.status === 0,
      user: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const generateInvite = async (inviteData, token) => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('email', inviteData.email);
    queryParams.append('groupId', inviteData.groupId);
    queryParams.append('type', inviteData.type);
    queryParams.append('base_url', inviteData.baseUrl || window.location.origin);

    if (inviteData.type === GROUP_TYPE.CLIENT) {
      queryParams.append('client_role', inviteData.role);
    } else if (inviteData.type === GROUP_TYPE.DEPARTMENT) {
      queryParams.append('department_role', inviteData.role);
    } else if (inviteData.type === GROUP_TYPE.TEAM) {
      queryParams.append('team_role', inviteData.role);
    }

    if (inviteData.service_roles?.length > 0) {
      inviteData.service_roles.forEach((role, index) => {
        queryParams.append(`service_roles[${index}].service_key`, role.service_key);
        queryParams.append(`service_roles[${index}].role_key`, role.role_key);
        if (role.service_id) {
          queryParams.append(`service_roles[${index}].service_id`, role.service_id);
        }
      });
    }

    if (inviteData.only_service_role_user !== undefined) {
      queryParams.append('only_service_role_user', inviteData.only_service_role_user);
    }

    const response = await axios.get(
      `${BASE_URL}/user/v1/user/invite?${queryParams.toString()}`,
      { headers: getHeaders(token) }
    );

    return {
      success: response.data.status === 0,
      inviteLink: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const acceptInvite = async (userData) => {
  try {
    const params = new URLSearchParams({
      first_name: userData.firstName,
      last_name: userData.lastName,
      username: userData.username || userData.email,
      password: userData.password,
      phone: userData.phone || '',
      company: userData.company || '',
      position: userData.position || ''
    });

    const response = await axios.get(
      `${BASE_URL}/user/public/v1/user/invitation/${userData.token}?${params.toString()}`
    );

    return {
      success: response.data.status === 0,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const updateUser = async (userData, token) => {
  try {
    const payload = {
      id: userData.id,
      first_name: userData.firstName,
      last_name: userData.lastName,
      phone: userData.phone,
      company: userData.company,
      position: userData.position,
      status: userData.status
    };

    if (userData.onlyServiceRoleUser !== undefined) {
      payload.only_service_role_user = userData.onlyServiceRoleUser;
    }

    const response = await axios.patch(
      `${BASE_URL}/user/v1/user/${userData.id}`,
      payload,
      { headers: getHeaders(token) }
    );

    return {
      success: response.data.status === 0,
      user: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const updateUserServiceRole = async (userId, serviceRole, token) => {
  try {
    const response = await axios.patch(
      `${BASE_URL}/user/v1/user/service/${userId}`,
      { id: userId, service_role: serviceRole },
      { headers: getHeaders(token) }
    );

    return {
      success: response.data.status === 0,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const acceptOnboarding = async (userId, groupId, groupType, token) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/user/v1/user/${userId}/onboarding/accept`,
      { user_id: userId, group_id: groupId, type: groupType },
      { headers: getHeaders(token) }
    );

    return {
      success: response.data.success,
      status: response.data.status,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const rejectOnboarding = async (userId, groupId, groupType, token) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/user/v1/user/${userId}/onboarding/reject`,
      { user_id: userId, group_id: groupId, type: groupType },
      { headers: getHeaders(token) }
    );

    return {
      success: response.data.success,
      status: response.data.status,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const listPendingRequests = async (groupType, groupId, pagination, token) => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('type', groupType);
    if (groupId) queryParams.append('group_id', groupId);
    if (pagination?.page) queryParams.append('pagination.page', pagination.page);
    if (pagination?.page_size) queryParams.append('pagination.page_size', pagination.page_size);

    const response = await axios.get(
      `${BASE_URL}/user/v1/user/requests?${queryParams.toString()}`,
      { headers: getHeaders(token) }
    );

    return {
      success: response.data.status === 0,
      requests: response.data.requests,
      pagination: response.data.pagination,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const updateUserRole = async (userId, groupId, groupType, role, token, isSuperAdmin = false) => {
  try {
    const payload = {
      user_id: userId,
      group_id: groupId,
      type: groupType,
      super_admin: isSuperAdmin
    };

    if (groupType === GROUP_TYPE.CLIENT) {
      payload.client_role = role;
    } else if (groupType === GROUP_TYPE.DEPARTMENT) {
      payload.department_role = role;
    } else if (groupType === GROUP_TYPE.TEAM) {
      payload.team_role = role;
    }

    const response = await axios.post(
      `${BASE_URL}/user/v1/user/role`,
      payload,
      { headers: getHeaders(token) }
    );

    return {
      success: response.data.status === 0,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const deleteUser = async (userId, token) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/user/v1/user/${userId}`,
      { headers: getHeaders(token) }
    );

    return {
      success: response.data.status === 0,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getUserCredentials = async (userId, token) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/user/v1/user/creds/${userId}`,
      { headers: getHeaders(token) }
    );

    return {
      success: response.data.status === 0,
      credentials: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getSuperUserCredentials = async (service, token) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/user/v1/super-user/creds/${service}`,
      { headers: getHeaders(token) }
    );

    return {
      success: response.data.status === 0,
      credentials: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getAllSuperUserCredentials = async (token) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/user/v1/super-user/all`,
      { headers: getHeaders(token) }
    );

    return {
      success: response.data.status === 0,
      credentials: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getUserGroups = async (userId, token) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/user/v1/users/${userId}/groups`,
      { headers: getHeaders(token) }
    );

    const data = response.data;
    return {
      success: data.status === 0,
      clients: (data.clients || []).map(c => ({
        id: c.id,
        role: getRoleValue(c, 'client')
      })),
      departments: (data.departments || []).map(d => ({
        id: d.id,
        role: getRoleValue(d, 'department')
      })),
      teams: (data.teams || []).map(t => ({
        id: t.id,
        role: getRoleValue(t, 'team')
      })),
      message: data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getAllUsers = async (params, token) => {
  try {
    const queryParams = new URLSearchParams();

    if (params.pagination) {
      queryParams.append('pagination.page', params.pagination.page || 1);
      queryParams.append('pagination.page_size', params.pagination.pageSize || 10);
    }

    if (params.sorting) {
      queryParams.append('sorting.sort_by', params.sorting.sort_by);
      queryParams.append('sorting.order', params.sorting.order);
    }

    if (params.query) queryParams.append('query', params.query);
    if (params.client_id) queryParams.append('client_id', params.client_id);
    else if (params.department_id) queryParams.append('department_id', params.department_id);
    else if (params.team_id) queryParams.append('team_id', params.team_id);
    if (params.enabled !== undefined) queryParams.append('enabled', params.enabled);

    const response = await axios.get(
      `${BASE_URL}/user/v1/users?${queryParams.toString()}`,
      { headers: getHeaders(token) }
    );

    const data = response.data;
    return {
      success: data.status === 0,
      users: data.data,
      totalCount: data.totalCount || 0,
      pageCount: data.pageCount || 0,
      page: data.page || 1,
      pageSize: data.pageSize || 10,
      message: data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const removeUserFromDepartment = async (departmentId, userId, token) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/user/v1/department/${departmentId}/user/${userId}`,
      { headers: getHeaders(token) }
    );

    return {
      success: response.data.status === 0,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const removeUserFromTeam = async (teamId, userId, token) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/user/v1/team/${teamId}/user/${userId}`,
      { headers: getHeaders(token) }
    );

    return {
      success: response.data.status === 0,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};
